import React from "react";
import {
  AutoCompleteComponent,
  AutoCompleteModel,
  ChangeEventArgs,
  FilteringEventArgs,
} from "@syncfusion/ej2-react-dropdowns";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base/src";
import {
  AsyncODataDropdownFieldCustomRenderProps,
  AsyncODataDropdownFieldProps,
  AsyncODataDropdownFieldTableRenderingColumnProps,
  AsyncODataDropdownFieldTableRenderProps,
} from "./AsyncODataDropdownField";
import { Predicate, Query } from "@syncfusion/ej2-data";
import { get, has } from "lodash";
import { useOData } from "../../../../stores/OData/useOData";
import { AsyncODataDropdownFieldValue } from "../../types/fieldTypes";
import { resolveODataUrl } from "../types";

export const useAsyncODataDropdownField = (
  props: AsyncODataDropdownFieldProps
) => {
  const {
    setupAsyncODataDropdownDataManager,
    baseActionCompleteEvent,
    baseActionFailureEvent,
  } = useOData(
    {
      getAuthRequestHeader:
        props.getAuthRequestHeader || (() => Promise.resolve({})),
    },
    () => {
      setInitialized(true);
    }
  );

  const autoCompleteRef = React.useRef<AutoCompleteComponent | null>(null);
  const [ready, setReady] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isDataLoaded, setIsDataLoaded] = React.useState<boolean>(false);
  const [initialized, setInitialized] = React.useState<boolean>(false);
  const [localValue, setLocalValue] = React.useState<any>(props.value);
  const [localData, setLocalData] = React.useState<any>();
  const [_localAllData, setLocalAllData] = React.useState<any>();
  const [autoCompleteSettings, setAutoCompleteSettings] = React.useState<
    AutoCompleteModel & DefaultHtmlAttributes
  >();

  const [queryKey, setQueryKey] = React.useState<string>();

  const textColumnName = props.textColumnName || "label";
  const valueColumnName = props.valueColumnName || "value";
  const groupByColumnName = props.groupByColumnName;
  const fieldsToSelect = [textColumnName, valueColumnName];
  const sortByColumnName = props.sortByColumnName || textColumnName;
  const sortDescending = !!props.sortDescending;
  const take = props.take || 100;

  const searchByPredicateFactory =
    props.searchByPredicateFactory ??
    ((searchText: string) =>
      new Predicate(textColumnName, "contains", searchText, true));

  const localValueLessThanMinLength =
    props.minLength && (!localValue || localValue.length < props.minLength);

  // Helper function to extract the correct value from props/newValue
  // If value is an object, extract the valueColumnName field
  // If value is a primitive, use it directly
  const extractValue = (value: AsyncODataDropdownFieldValue) => {
    if (value && typeof value === "object" && valueColumnName in value) {
      return value[valueColumnName];
    }
    return value;
  };

  const handleValueChange = (newValue: AsyncODataDropdownFieldValue) => {
    if (initialized && autoCompleteRef.current) {
      setLocalValue(newValue);

      const extractedValue = extractValue(newValue);

      if (extractedValue && !autoCompleteRef.current.value) {
        autoCompleteRef.current.value = extractedValue;
      }
    }
  };

  React.useEffect(() => {
    setQueryKey("initial-key");
  }, [initialized]);

  React.useEffect(() => {
    if (initialized && autoCompleteRef.current) {
      handleValueChange(props.value);
    }
  }, [props.value, initialized]);

  /** Observe and Save Filter Settings */
  React.useEffect(() => {
    if (initialized) {
      setup();
    }
  }, [queryKey]);

  React.useEffect(() => {
    const requestKey = JSON.stringify(buildQuery());
    if (requestKey !== queryKey) {
      setQueryKey(requestKey);
    }
  }, [props.predicate]);

  React.useEffect(() => {
    if (initialized && props.onSubmit) {
      // For schema-driven fields (using baseODataUrl + oDataKey), return primitive value
      // For legacy fields (using odataUrl), return full object for backward compatibility
      if (props.baseODataUrl && props.oDataKey) {
        // Schema-driven field - return primitive value (ID only)
        const primitiveValue = localData
          ? get(localData, valueColumnName)
          : undefined;
        props.onSubmit(primitiveValue);
      } else {
        // Legacy field - return full object
        props.onSubmit(localData);
      }
    }
  }, [localData]);

  const showSearchPopup = () => {
    if (
      !!autoCompleteRef.current &&
      initialized &&
      !props.disabled &&
      !isLoading &&
      !localValueLessThanMinLength
    ) {
      // Due to a bug in the Syncfusion library, we need to set the queryString to null to avoid the popup from
      // remembering the previous filter search string and filtering on value.
      const autoCompleteRefNoProtection = autoCompleteRef.current as any;
      autoCompleteRefNoProtection.queryString = null;
      autoCompleteRef.current.showPopup();
    }
  };

  const buildQuery = (searchText?: string): Query => {
    let query = new Query().select(fieldsToSelect);

    // Add predicates

    if (props.predicate) {
      query = query.where(props.predicate);
    }

    if (searchText) {
      query = query.where(searchByPredicateFactory(searchText));
    }

    // Sort and limit

    query = query
      .sortBy(sortByColumnName, sortDescending ? "descending" : "ascending")
      .take(take);

    return query;
  };

  const handleChange = async (args: ChangeEventArgs) => {
    if (!!autoCompleteRef.current && initialized && args.isInteracted) {
      if (!args?.value) {
        /** If localData is undefined it never triggers the update, in which case we trigger in this clause */
        if (!!localData) {
          setLocalData(undefined);
        } else {
          props.onSubmit && props.onSubmit(undefined);
        }
      } else {
        setLocalData(args.itemData);
      }
    }
  };

  const handleFiltering = (args: FilteringEventArgs) => {
    args.preventDefaultAction = true;

    if (autoCompleteRef.current && initialized && !isLoading) {
      let query = buildQuery(args.text);
      args.updateData(autoCompleteRef.current.dataSource, query);
    }
  };

  const defaultAutoCompleteSettings: AutoCompleteModel & DefaultHtmlAttributes =
    {
      autofill: true,
      showClearButton: true,
      highlight: false,
      allowCustom: false,
      fields: {
        text: textColumnName,
        value: valueColumnName,
        groupBy: groupByColumnName,
      },

      /** Inmutable */
      focus: showSearchPopup,
      onClick: showSearchPopup,
      change: handleChange,
      filtering: handleFiltering,
      actionBegin: (args: any) => {
        // Set the query so we can apply the filtering logic ourselves.
        args.query = args.query || buildQuery();
        setIsLoading(true);
      },
      actionFailure: (args: Object) => {
        baseActionFailureEvent(() => 0)(args);
        setIsLoading(false);
      },
      actionComplete: (args: Object) => {
        const data = get(args, "result", []);
        setLocalAllData(data);
        baseActionCompleteEvent();
        setIsLoading(false);
      },
    };

  const setup = () => {
    const settings = {
      ...defaultAutoCompleteSettings,
      ...(props.autoCompleteSettings || {}),
    } as AutoCompleteModel & DefaultHtmlAttributes;

    if (!props.renderProps || has(props.renderProps, "columnsToDisplay")) {
      const tableRenderProps =
        props.renderProps as AsyncODataDropdownFieldTableRenderProps;

      const columnsToDisplay = tableRenderProps?.columnsToDisplay || {
        [textColumnName]: {
          headerText: "Please select a value",
        } as AsyncODataDropdownFieldTableRenderingColumnProps,
      };

      if (tableRenderProps?.showHeader !== false) {
        const defaultHeaderRenderer = (text: string) => (
          <span style={{ fontWeight: "bold" }}>{text}</span>
        );

        const headerRenderer =
          tableRenderProps?.headerRenderer ?? defaultHeaderRenderer;

        settings.headerTemplate = () => (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${
                Object.keys(columnsToDisplay).length
              }, 1fr)`,
            }}
          >
            {Object.entries(columnsToDisplay).map(
              ([columnName, displaySettings]) => (
                <div key={columnName}>
                  {headerRenderer(displaySettings?.headerText ?? columnName)}
                </div>
              )
            )}
          </div>
        );
      }

      const defaultColumnRenderer = (text: string, _item: any) => (
        <span>{text}</span>
      );

      const getColumnRenderer = (
        displaySettings: AsyncODataDropdownFieldTableRenderingColumnProps
      ) =>
        displaySettings?.columnRenderer ??
        tableRenderProps?.columnRenderer ??
        defaultColumnRenderer;

      settings.itemTemplate = (data: any) => (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${
              Object.keys(columnsToDisplay).length
            }, 1fr)`,
          }}
        >
          {Object.entries(columnsToDisplay).map(
            ([columnName, displaySettings]) => (
              <div key={columnName}>
                {getColumnRenderer(displaySettings)(data[columnName], data)}
              </div>
            )
          )}
        </div>
      );

      if (groupByColumnName) {
        const defaultGroupRenderer = (groupName: string, _groupData: any) => (
          <span style={{ fontWeight: "bold" }}>{groupName}</span>
        );

        const groupRenderer =
          tableRenderProps?.groupRenderer ?? defaultGroupRenderer;

        settings.groupTemplate = (data: any) =>
          groupRenderer(data[groupByColumnName], data);
      }
    } else {
      const customRenderProps =
        props.renderProps as AsyncODataDropdownFieldCustomRenderProps;
      settings.headerTemplate = customRenderProps.headerRenderer;
      settings.itemTemplate = customRenderProps.itemRenderer;
    }

    const finalODataUrl = resolveODataUrl(props);

    settings.dataSource = setupAsyncODataDropdownDataManager(
      finalODataUrl,
      props.transformData
    );
    settings.value = extractValue(props.value);

    setAutoCompleteSettings(settings);
    setReady(true);
  };

  return {
    autoCompleteRef,
    ready,
    isLoading,
    setIsLoading,
    isDataLoaded,
    setIsDataLoaded,
    localValue,
    setLocalValue,
    autoCompleteSettings,
    handleValueChange,
    buildQuery,
  };
};
